import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from './offer'
import { INTL_DAY_MONTH_NAME_SHORT } from './dateFormats'
import { OfferContextMap } from 'analytics/snowplow/context'
import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import Places from 'constants/places'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

export const TOUR_CHILD_MAX_AGE = 15
export const TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD = 105
export const TOUR_HANDBACK_URGENCY_MAX_DAYS = 14

export const DIRECT_TOUR = 'direct_tour'
export const PARTNER_TOUR = 'partner_tour'
export const CONNECTION_TOUR = 'connection_tour'
export const CRUISE_TOUR = 'cruise_tour'

export const ULTRA_LUX_TOUR = 'ultra_lux_tour'

export const TOUR_ARTEFACT_SIZE_MAX_BYTES = 5 * 1024 * 1024
export const TOUR_ARTEFACT_UPLOAD_DEADLINE_DAYS_BEFORE_DEPARTURE = 7
export const TOUR_ARTEFACT_RETENTION_DAYS_AFTER_DEPARTURE = 7

/** Customer self-service edits to submitted tour traveller details are allowed until this many hours before departure (instant on stored departure timestamp). */
export const TOUR_TRAVELLER_SELF_EDIT_MIN_HOURS_BEFORE_DEPARTURE = 72

export const TOUR_V2_OFFER_LIST_TYPES: Array<App.Tours.V2ParentOfferType> = [OFFER_TYPE_TOUR_V2]
export const TOUR_V1_OFFER_LIST_TYPES: Array<App.OfferType> = [OFFER_TYPE_TOUR]
export const TOUR_V1_OFFER_LIST_FILTER_TYPES: Array<App.OfferType> = [OFFER_TYPE_TOUR]
export const TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES = new Set([
  'qantas',
  'velocity',
])

export const TOUR_ORDER_TRAVELLER_DETAILS_TTL: number = 300 // in seconds

export enum TourV2ContentSections {
  GALLERY = 'gallery',
  OVERVIEW = 'overview',
  ITINERARY = 'itinerary',
  VARIATIONS = 'variations',
  PREFERRED_PACKAGES = 'preferred-packages',
  INCLUSIONS = 'inclusions',
  DEPARTURES = 'dates-and-pricing',
  DEPARTURE_DATES = 'departure_dates',
  FINE_PRINT = 'things-to-know',
  FAQ = 'frequently-asked-questions',
  YOU_MAY_ALSO_LIKE = 'you-may-also-like',
  REVIEWS = 'reviews',
  ADDITIONAL_WAYS = 'additional-ways',
  SIMILAR_TOURS = 'similar-tours',
}

export const TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS = new Set<TourV2ContentSections>([
  TourV2ContentSections.OVERVIEW,
  TourV2ContentSections.ITINERARY,
  TourV2ContentSections.VARIATIONS,
  TourV2ContentSections.PREFERRED_PACKAGES,
  TourV2ContentSections.INCLUSIONS,
  TourV2ContentSections.DEPARTURES,
  TourV2ContentSections.FINE_PRINT,
  TourV2ContentSections.FAQ,
  TourV2ContentSections.REVIEWS,
])

export const TOUR_V2_TARGET_OCCUPANT_TYPES: Set<App.OccupantType> = new Set(['adults', 'children'])

export const TOUR_V2_SEARCH_TYPES: Array<App.SearchType> = ['destination', 'tour']

export const TOUR_V2_SEARCH_HERO_IMAGE_ID = 'adkvxdgnii1kvb4s970c'
export const TOUR_V2_CATALOGUE_HERO_IMAGE_ID = 'tour-catalog-header'
export const TOUR_V2_SEARCH_HERO_IMAGE_ASIA_CAMPAIGN = 'tour-hero-asia-campaign-background-v2'
export const TOUR_V2_HERO_IMAGE_ASIA_MOBILE_BACKGROUND = 'tour-hero-asia-campaign-mobile-background-v2'
export const TOUR_V2_PROFILE_ASIA_CAMPAIGN = 'profile-asia-campaign'
export const TOUR_ASIA_CAMPAIGN = 'TOURASIA'
export const TOUR_PROMO_CODE = 'TOURSALE'
export const TOUR_PROMO_END_DATE = '2025-01-31T00:00:00Z'

const ARCTIC_CHARTER_DEPOSIT_AMOUNT = formatCurrency(1000, 'AUD', 'AU', { format: 'roundedDollar' })

const tourCampaignMessages = defineMessages({
  name: {
    id: '8XiFvzWLQRvoChiy6MGP',
    defaultMessage: 'Tours Early Bird',
    description: 'Campaign name for the tours early bird flight voucher campaign on promotional banners and modals',
  },
  termsAndConditions: {
    id: 'JszFF6EEMdByURCBd9y4',
    defaultMessage: 'Book a Qualifying Luxury Escapes Tour between 12 Oct and 23 Nov 2026 and get a flight voucher worth A$250 Economy, A$350 Premium Economy or A$500 Business per passenger, for up to your number of Tour travellers. Redeem on 1 international return booking from Australia on Emirates, Qatar Airways or Singapore Airlines via Luxury Escapes by 28 Feb 2027. Travel can be later, subject to availability. Excludes domestic and NZ travel. Single use, no cash value, unused value forfeited, not combinable with other offers. Full terms apply.',
    description: 'Terms and conditions for the tours early bird flight voucher campaign, shown in the modal opened from the campaign banner',
  },
  tourTermsAndConditions: {
    id: 'DmdJGlup5thMOYkKCoRN',
    defaultMessage: 'Book this tour between 12 Oct and 23 Nov 2026 and get a flight voucher worth A$250 Economy, A$350 Premium Economy or A$500 Business per passenger, for up to your number of Tour travellers. Redeem on 1 international return booking from Australia on Emirates, Qatar Airways or Singapore Airlines via Luxury Escapes by 28 Feb 2027. Travel can be later, subject to availability. Excludes domestic and NZ travel. Single use, no cash value, unused value forfeited, not combinable with other offers. Full terms apply.',
    description: 'Terms and conditions for the tours early bird flight voucher campaign, shown in the modal opened from the flight voucher inclusion on an eligible tour',
  },
  tierName: {
    id: 'SjHnekdZ-8ZU5-DacFR0',
    defaultMessage: 'Primary campaign offer',
    description: 'Internal-facing tier name for the reusable tours campaign offer treatment',
  },
})

const tourTravelTagLabelMessages = defineMessages({
  internationalFlightsIncluded: {
    id: 'mD8RfLyRh0KWq4B848HC',
    defaultMessage: 'International Flights Included',
    description: 'Travel tag label for the Arctic Charter tour indicating international flights are included',
  },
  arcticCharterDeposit: {
    id: 'gihpQfpwlYj9ucgPyTUZ',
    defaultMessage: 'Secure with {amount} deposit',
    description: 'Travel tag label for the Arctic Charter tour indicating the deposit amount',
  },
})

const tourTravellerTitleMessages = defineMessages({
  mr: {
    id: 'l8mFnmr2qvFYOKxJvVWp',
    defaultMessage: 'Mr',
    description: 'Tour traveller honourific title label for adult male travellers in tour booking forms',
  },
  master: {
    id: 'dG95R6XGR_xIGj_9gRDy',
    defaultMessage: 'Master',
    description: 'Tour traveller honourific title label for male child travellers in tour booking forms',
  },
  ms: {
    id: 'XDZ20aRgJrlrJnQsI14-',
    defaultMessage: 'Ms',
    description: 'Tour traveller honourific title label for adult female travellers in tour booking forms',
  },
  mrs: {
    id: 'fm43a7O_2IfSa8jOGpBJ',
    defaultMessage: 'Mrs',
    description: 'Tour traveller honourific title label for married female travellers in tour booking forms',
  },
  miss: {
    id: 'eArow3xI2q_LQSjxzLYv',
    defaultMessage: 'Miss',
    description: 'Tour traveller honourific title label for unmarried female travellers in tour booking forms',
  },
})

const tourPackageOptionSourceMessages = defineMessages({
  base: {
    id: 'xYXjlXwg_zUBu0veHinI',
    defaultMessage: 'Base',
    description: 'Salesforce source name for the base tour package option used in API matching and variation labels',
  },
  extended: {
    id: '_5qo8VC440EcRKXLo3EM',
    defaultMessage: 'Extended',
    description: 'Salesforce source name for the extended tour package option used in API matching and variation labels',
  },
  extendedAlternative: {
    id: 'haXW48ivMI_BDYU8-VBI',
    defaultMessage: 'Extended Alternative',
    description: 'Salesforce source name for the alternative extended tour package option used in API matching',
  },
})

const tourPromoCodeMessages = defineMessages({
  urgency: {
    id: 'MnmNqXOsuRsKEO41ggSz',
    defaultMessage: 'Code: {promoCode} ends {endDate}',
    description: 'Urgency message shown on tours offers with the promo code and its ending date',
  },
})

const tourPackageUpgradeDisplayMessages = defineMessages({
  standardRegular: {
    id: 'b9palHdolSvMp-IlZZ2i',
    defaultMessage: 'Standard Package',
    description: 'Default package upgrade label for the regular tour package option',
  },
  standardVip: {
    id: 'h2_EtFC2HWhSVQilUwNQ',
    defaultMessage: 'Upgraded Package',
    description: 'Default package upgrade label for the VIP tour package option',
  },
  standardPlatinum: {
    id: 'jzV5qmTIVdVTCeKuqzLS',
    defaultMessage: 'Deluxe Package',
    description: 'Default package upgrade label for the platinum tour package option',
  },
  ecoBusinessRegular: {
    id: '4I2qlEMsRpcT93YPf1s2',
    defaultMessage: 'Economy Package',
    description: 'Special package upgrade label for the regular tour package option on economy/business style tours',
  },
  ecoBusinessVip: {
    id: 'qXZu8tX8sT1bYL8T4v2l',
    defaultMessage: 'Business Package',
    description: 'Special package upgrade label for the VIP tour package option on economy/business style tours',
  },
  ecoBusinessPlatinum: {
    id: 'Ih1cWqlfC1yBu12IQMrl',
    defaultMessage: 'First Class Package',
    description: 'Special package upgrade label for the platinum tour package option on economy/business style tours',
  },
  viewRegular: {
    id: 'YiMyIVtwzdPrffMBefv_',
    defaultMessage: 'King or Twin Package',
    description: 'Package upgrade label for the regular tour package option on room-view tour variants',
  },
  viewVip: {
    id: 'eh62fJvhKPjvq2vGL5fq',
    defaultMessage: 'Ocean View w. Club Access Upgrade',
    description: 'Package upgrade label for the VIP tour package option on room-view tour variants',
  },
  viewPlatinum: {
    id: 'KuSXMjpOM85v7PM4_QRw',
    defaultMessage: 'Regency Suite w. Club Access Upgrade',
    description: 'Package upgrade label for the platinum tour package option on room-view tour variants',
  },
  rangeViewRegular: {
    id: 'nZiKFk8reb-EFci5sdrq',
    defaultMessage: 'Classic to King or Twin Package',
    description: 'Package upgrade label for the regular tour package option on room-range tour variants',
  },
  rangeViewVip: {
    id: 'xPT7_Lg-DWkegz1fhgO-',
    defaultMessage: 'Superior to Ocean View w. Club Access Upgrade',
    description: 'Package upgrade label for the VIP tour package option on room-range tour variants',
  },
  rangeViewPlatinum: {
    id: '7FPVBz9Ex7TA4dhDwHSx',
    defaultMessage: 'Deluxe to Regency Suite w. Club Access Upgrade',
    description: 'Package upgrade label for the platinum tour package option on room-range tour variants',
  },
})

export const TOUR_CAMPAIGN_CONFIG = {
  tag: 'Tours Early Bird',
  brands: [LUXURY_ESCAPES],
  startDate: '2026-10-12T00:00:00+11:00',
  endDate: '2026-11-23T23:59:59+11:00',
  regionCode: 'AU',
  searchFilterTag: 'Tours Early Bird',
  flightCredit: {
    maxValuePerPerson: 500,
    currencyCode: 'AUD',
  },
  banner: {
    slides: [
      { imageId: '895c9unlpuq9npgmh3wf' },
      { imageId: 'qos5u1fv6o4duhr3ya6' },
    ],
  },
  tiers: [
    {
      id: 'primary',
      tourIds: [],
    },
  ],
} satisfies Omit<App.Tours.TourCampaign, 'name' | 'tiers'> & {
  tiers: Array<Omit<App.Tours.TourCampaignTier, 'name' | 'tag' | 'termsAndConditions'>>
}

export const TOUR_CAMPAIGN_SEARCH_FILTER_TAG = TOUR_CAMPAIGN_CONFIG.searchFilterTag ?? TOUR_CAMPAIGN_CONFIG.tag

export function getTourCampaignTourTermsAndConditions(intl: IntlShape = getCurrentIntl()): string {
  return intl.formatMessage(tourCampaignMessages.tourTermsAndConditions)
}

export function getTourCampaignConfig(intl: IntlShape = getCurrentIntl()): App.Tours.TourCampaign {
  const termsAndConditions = intl.formatMessage(tourCampaignMessages.termsAndConditions)

  return {
    ...TOUR_CAMPAIGN_CONFIG,
    name: intl.formatMessage(tourCampaignMessages.name),
    tiers: TOUR_CAMPAIGN_CONFIG.tiers.map((tier) => ({
      ...tier,
      name: intl.formatMessage(tourCampaignMessages.tierName),
      termsAndConditions,
    })),
  }
}

export type TourSearchSpecialOfferCampaignIntlKey = 'toursCampaign'

export interface TourSearchSpecialOfferCampaignConfig {
  brands?: Array<string>
  startDate?: string
  endDate?: string
  intlKey: TourSearchSpecialOfferCampaignIntlKey
  regionCodes?: Array<string>
  tag: string
}

export const TOUR_SEARCH_SPECIAL_OFFERS_CAMPAIGNS: Array<TourSearchSpecialOfferCampaignConfig> = [
  {
    tag: TOUR_CAMPAIGN_SEARCH_FILTER_TAG,
    intlKey: 'toursCampaign',
    startDate: TOUR_CAMPAIGN_CONFIG.startDate,
    endDate: TOUR_CAMPAIGN_CONFIG.endDate,
    brands: TOUR_CAMPAIGN_CONFIG.brands,
    regionCodes: TOUR_CAMPAIGN_CONFIG.regionCode ? [TOUR_CAMPAIGN_CONFIG.regionCode] : undefined,
  },
]

export interface TourStyleGroupBenefitVisibilityConfig {
  enabled: boolean
  tourIds: Array<string>
}

/**
 * When enabled, the configured tours will hide the max group size benefit tile
 * from the styled tour offer page key benefits section.
 */
export const TOUR_STYLE_GROUP_BENEFIT_VISIBILITY_CONFIG: TourStyleGroupBenefitVisibilityConfig = {
  enabled: true,
  tourIds: [
    'tour-1c41669f-b341-4fca-9f97-79b44b7b1df9', // Staging
    'tour-3cf2944d-b311-41f6-a90c-5cebf616fabf',
    'tour-63ec2a75-fd6c-4f43-8b24-691353bd96ce',
    'tour-746c6cdf-30b5-4402-9760-efdfd2ba2da3', // Great Southern Brisbane to Adelaide, City Stays
    'tour-703674c1-a500-4ce2-a657-a0a4aa41ccb8', // Great Southern Adelaide to Brisbane 2N, City Stays
    'tour-3e38352f-dd6b-40f5-a01d-4ce85fd124b4', // Scenic Eclipse II Patagonia Cruise & Atacama Desert Lodge
  ],
}

export function shouldDisplayTourStylesGroupBenefit(
  tourId: string,
  config: TourStyleGroupBenefitVisibilityConfig = TOUR_STYLE_GROUP_BENEFIT_VISIBILITY_CONFIG,
): boolean {
  return !config.enabled || !config.tourIds.includes(tourId)
}

export function getTourPromoCodeUrgencyMessage(): string {
  const intl = getCurrentIntl()

  return intl.formatMessage(
    tourPromoCodeMessages.urgency,
    {
      promoCode: TOUR_PROMO_CODE,
      endDate: intlDateFormatter(
        TOUR_PROMO_END_DATE,
        INTL_DAY_MONTH_NAME_SHORT,
        intl.locale,
        'UTC',
      ),
    },
  )
}

export function isTourCampaignTaggedOffer(campaignTags?: Array<string>): boolean {
  return Boolean(campaignTags?.includes(TOUR_CAMPAIGN_CONFIG.tag))
}

export function getTourCampaignTier(
  campaignTags?: Array<string>,
  intl: IntlShape = getCurrentIntl(),
): App.Tours.TourCampaignTier | undefined {
  if (!isTourCampaignTaggedOffer(campaignTags)) {
    return undefined
  }

  return getTourCampaignConfig(intl).tiers[0]
}

interface TourTravelTagLabelConfig {
  label: MessageDescriptor
  values?: Record<string, string>
  showIcon?: boolean
  tourIds: Array<string>
}

const TOUR_TRAVEL_TAG_LABELS_CONFIG: Array<TourTravelTagLabelConfig> = [
  {
    label: tourTravelTagLabelMessages.internationalFlightsIncluded,
    tourIds: [
      'tour-6a4b516e-8398-4bfc-8d76-aaa15a957bd5', // Arctic Charter
    ],
  },
  {
    label: tourTravelTagLabelMessages.arcticCharterDeposit,
    values: {
      amount: ARCTIC_CHARTER_DEPOSIT_AMOUNT,
    },
    showIcon: false,
    tourIds: [
      'tour-6a4b516e-8398-4bfc-8d76-aaa15a957bd5', // Arctic Charter
    ],
  },
]

export interface TourTravelTagLabel {
  label: string
  showIcon: boolean
}

export function getTourTravelTags(
  tourId: string,
  intl: IntlShape = getCurrentIntl(),
): Array<TourTravelTagLabel> {
  return TOUR_TRAVEL_TAG_LABELS_CONFIG
    .filter(({ tourIds }) => tourIds.includes(tourId))
    .map(({ label, values, showIcon = true }) => ({
      label: intl.formatMessage(label, values),
      showIcon,
    }))
}

export function getTourTravelTagLabels(
  tourId: string,
  intl: IntlShape = getCurrentIntl(),
): Array<string> {
  return getTourTravelTags(tourId, intl).map(({ label }) => label)
}

function isWithinTourCampaignWindow(startDate?: string, endDate?: string): boolean {
  const now = Date.now()
  const startTime = startDate ? new Date(startDate).getTime() : undefined
  const endTime = endDate ? new Date(endDate).getTime() : undefined

  if (startTime !== undefined && (Number.isNaN(startTime) || now < startTime)) {
    return false
  }

  return endTime === undefined || (!Number.isNaN(endTime) && now <= endTime)
}

export function isTourSearchSpecialOfferCampaignActive(
  campaign: TourSearchSpecialOfferCampaignConfig,
  regionCode: string,
  brand: string,
): boolean {
  if (campaign.brands?.length && !campaign.brands.includes(brand)) {
    return false
  }

  if (campaign.regionCodes?.length && !campaign.regionCodes.includes(regionCode)) {
    return false
  }

  return isWithinTourCampaignWindow(campaign.startDate, campaign.endDate)
}

export function isTourCampaignActive(regionCode?: string, brand?: string): boolean {
  const isBrandAllowed = !TOUR_CAMPAIGN_CONFIG.brands?.length
    || Boolean(brand && TOUR_CAMPAIGN_CONFIG.brands.includes(brand))

  return isBrandAllowed
    && regionCode === TOUR_CAMPAIGN_CONFIG.regionCode
    && isWithinTourCampaignWindow(TOUR_CAMPAIGN_CONFIG.startDate, TOUR_CAMPAIGN_CONFIG.endDate)
}

export const TOUR_CAMPAIGN_FLIGHT_CREDIT_INCLUSION_ID = 'tour-campaign-flight-credit'

export const tourCampaignFlightCreditInclusionMessage = defineMessages({
  description: {
    id: 'JdpxtlVbYTX2N5NGxgAv',
    defaultMessage: 'Up to A$500 flight voucher per person. <link>T&Cs Apply.</link>',
    description: 'Limited time inclusion on tours eligible for the tours early bird flight voucher campaign, with a link to the voucher terms',
  },
}).description

export function hasTourCampaignFlightCreditInclusion(inclusions: Array<App.OfferInclusion>): boolean {
  return inclusions.some(inclusion => inclusion.id === TOUR_CAMPAIGN_FLIGHT_CREDIT_INCLUSION_ID)
}

export function getTourCampaignFlightCreditInclusion(
  regionCode?: string,
  campaignTags?: Array<string>,
  intl: IntlShape = getCurrentIntl(),
): App.OfferInclusion | undefined {
  if (!isTourCampaignActive(regionCode, config.BRAND) || !isTourCampaignTaggedOffer(campaignTags)) {
    return undefined
  }

  return {
    id: TOUR_CAMPAIGN_FLIGHT_CREDIT_INCLUSION_ID,
    description: intl.formatMessage(tourCampaignFlightCreditInclusionMessage, { link: chunks => chunks.join('') }),
    symbol: 'plane_with_plus',
    isFlash: true,
  }
}

export const TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES: Array<App.SearchPlaceType> = [
  'city',
  'colloquial_area',
  'continent',
  'country',
  'high_level_region',
  'multi_city_vicinity',
  'province_state',
]

export const TOUR_MINIMUM_VALUED_AT_PERCENTAGE_TO_SHOW = 5

export const DEFAULT_TOUR_V2_OFFER_MAX_CHILD_AGE = 17

export const DEFAULT_TOUR_V2_PRIVATE_MAX_CHILD_AGE = 15

export const VendorsThatRequireTravellerDetails = new Set(['00128000018BZkIAAW', '0010I00001ba7JdQAI', '0010I00001f6bHvQAI', '0010I000028DrsxQAC', '0012y00000LHHZoAAP'])

export const TOUR_V2_MALE_TRAVELLER_TITLES: Array<App.Title> = [
  tourTravellerTitleMessages.mr.defaultMessage as App.Title,
  tourTravellerTitleMessages.master.defaultMessage as App.Title,
]

export const TOUR_V2_FEMALE_TRAVELLER_TITLES: Array<App.Title> = [
  tourTravellerTitleMessages.ms.defaultMessage as App.Title,
  tourTravellerTitleMessages.mrs.defaultMessage as App.Title,
  tourTravellerTitleMessages.miss.defaultMessage as App.Title,
]

export const TOUR_V2_LE_SOURCE = 'salesforce'

export const TOUR_V2_PACKAGE_OPTION_BASE = tourPackageOptionSourceMessages.base.defaultMessage
export const TOUR_V2_PACKAGE_OPTION_EXTENDED = tourPackageOptionSourceMessages.extended.defaultMessage
export const TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE = tourPackageOptionSourceMessages.extendedAlternative.defaultMessage

export const tourOptionPackages = [
  TOUR_V2_PACKAGE_OPTION_BASE,
  TOUR_V2_PACKAGE_OPTION_EXTENDED,
  TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE,
]

export const tourOptionPackagesEvent = {
  [TOUR_V2_PACKAGE_OPTION_BASE]: OfferContextMap.VIEW_TOUR_OPTIONS_PRESSED,
  [TOUR_V2_PACKAGE_OPTION_EXTENDED]: OfferContextMap.VIEW_TOUR_OPTIONS_EXTENDED_PRESSED,
  [TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE]: OfferContextMap.VIEW_TOUR_OPTIONS_EXTENDED_ALT_PRESSED,
}

export enum PACKAGE_UPGRADE_VALUES {
  REGULAR = 'regular',
  VIP = 'vip',
  PLATINUM = 'platinum',
}

export const TourOptionPackageUpgrades = [PACKAGE_UPGRADE_VALUES.REGULAR, PACKAGE_UPGRADE_VALUES.VIP, PACKAGE_UPGRADE_VALUES.PLATINUM]

export const SUPERIOR_PACKAGE_OPTIONS_AVAILABLES: Record<string, Array<PACKAGE_UPGRADE_VALUES>> = {
  [PACKAGE_UPGRADE_VALUES.REGULAR]: TourOptionPackageUpgrades,
  [PACKAGE_UPGRADE_VALUES.VIP]: [PACKAGE_UPGRADE_VALUES.VIP, PACKAGE_UPGRADE_VALUES.PLATINUM],
  [PACKAGE_UPGRADE_VALUES.PLATINUM]: [PACKAGE_UPGRADE_VALUES.PLATINUM],
}

export const ALL_TOUR_PACKAGE_TYPE = 'all'

export const getOfferPackageUpgradeDisplayTextMap = (offerId: string) => {
  const intl = getCurrentIntl()
  const defaultPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage(tourPackageUpgradeDisplayMessages.standardRegular)],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage(tourPackageUpgradeDisplayMessages.standardVip)],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage(tourPackageUpgradeDisplayMessages.standardPlatinum)],
  ])
  const ecoBusinessPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage(tourPackageUpgradeDisplayMessages.ecoBusinessRegular)],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage(tourPackageUpgradeDisplayMessages.ecoBusinessVip)],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage(tourPackageUpgradeDisplayMessages.ecoBusinessPlatinum)],
  ])
  const viewPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage(tourPackageUpgradeDisplayMessages.viewRegular)],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage(tourPackageUpgradeDisplayMessages.viewVip)],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage(tourPackageUpgradeDisplayMessages.viewPlatinum)],
  ])
  const rangeViewPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage(tourPackageUpgradeDisplayMessages.rangeViewRegular)],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage(tourPackageUpgradeDisplayMessages.rangeViewVip)],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage(tourPackageUpgradeDisplayMessages.rangeViewPlatinum)],
  ])

  switch (offerId?.replace('tour-', '')) {
    case 'bbf97caa-d857-4517-b768-465f5ceac8db':
    case '15275af7-48cb-4a5e-ad0c-eb9b3efc1619':
    case '6f77e732-d5e1-4747-ae47-901022e36fc3':
      return ecoBusinessPackageUpgradeDisplayTextMap
    case '6909c91b-cc92-4c0b-86d4-d663263436be':
      return viewPackageUpgradeDisplayTextMap
    case '1ea35824-8354-4656-9d76-c24d717ee700':
      return rangeViewPackageUpgradeDisplayTextMap
    default:
      return defaultPackageUpgradeDisplayTextMap
  }
}

export enum TOURS_GROUP_TYPE {
  isPrivateRequest = 'isPrivateRequest',
  isSmallGroup = 'isSmallGroup',
  isLargeGroup = 'isLargeGroup',
  isFamilyFriendly = 'isFamilyFriendly',
}

export const TOURS_GROUP_TYPES_LIST = Object.values(TOURS_GROUP_TYPE)

export enum TOUR_OFFER_TAG {
  onSale = 'onSale',
  flashOffer = 'flashOffer',
}

export const TOUR_OFFER_TAG_LIST = Object.values(TOUR_OFFER_TAG)

export const TOUR_MAX_ROOM_LIMIT = 5

export const TOUR_OPTIONAL_EXPERIENCE_BOOKING_DAYS_BEFORE_DEPARTURE = 60

export const TOUR_OPTIONAL_EXPERIENCE_RESERVATION_STATUS_CONFIRMED = 'confirmed'
export const TOUR_OPTIONAL_EXPERIENCE_ITEM_STATUS_COMPLETED = 'completed'

export const BRAND_SIGNATURE_SERIES = 'lesignaturetours'

export const PROMO_CODE_MAX_DISCOUNT_REGION: Record<string, string> = {
  AU: formatCurrency(1000, 'AUD', 'AU', { format: 'dynamic' }),
  NZ: formatCurrency(1000, 'NZD', 'NZ', { format: 'dynamic' }),
  SG: formatCurrency(1000, 'SGD', 'SG', { format: 'dynamic' }),
  US: formatCurrency(600, 'USD', 'US', { format: 'dynamic' }),
  GB: formatCurrency(500, 'GBP', 'GB', { format: 'dynamic' }),
  HK: formatCurrency(5000, 'HKD', 'HK', { format: 'dynamic' }),
}

export const PROMO_SPEND_EARN_REGION: Record<string, Array<{ spend: number, earn: number }>> = {
  AU: [{ spend: 4999, earn: 250 }, { spend: 6999, earn: 300 }, { spend: 9999, earn: 500 }, { spend: 19999, earn: 750 }],
  NZ: [{ spend: 5499, earn: 250 }, { spend: 7499, earn: 300 }, { spend: 10499, earn: 500 }, { spend: 15999, earn: 750 }],
  SG: [{ spend: 4499, earn: 250 }, { spend: 6499, earn: 300 }, { spend: 9499, earn: 500 }, { spend: 13999, earn: 750 }],
  HK: [{ spend: 24999, earn: 1250 }, { spend: 33999, earn: 1500 }, { spend: 49999, earn: 2500 }, { spend: 74999, earn: 3750 }],
  GB: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 149 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  US: [{ spend: 2299, earn: 150 }, { spend: 4499, earn: 200 }, { spend: 6499, earn: 300 }, { spend: 12999, earn: 500 }],
  CA: [{ spend: 2299, earn: 150 }, { spend: 4499, earn: 200 }, { spend: 6499, earn: 300 }, { spend: 12999, earn: 500 }],
  FR: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  IT: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  ES: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  NL: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  DE: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  IE: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
}

export const EXCLUDE_OFFER_PROMO_SPEND_EARN = ['tour-b9fcebec-2e68-42d5-a5b5-b09c69930d87']
export const END_SPEND_EARN_PROMO_TAG_DATE = new Date('2025-03-10')

export const TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS = [
  Places.India.id,
  Places.SriLanka.id,
  Places.Thailand.id,
  Places.Vietnam.id,
  Places.Japan.id,
  Places.China.id,
  Places.Malaysia.id,
  Places.Nepal.id,
]
export const TOUR_ASIA_TAKEOVER_SHOW_SEARCH_BANNER_PLACE_IDS = [
  ...TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
  Places.Anywhere.id,
  Places.Asia.id,
  Places.SouthEastAsia.id,
]

export const TOUR_HERO_IMAGES_IDS_ROTATION: Array<{ id: string, gravity: 'north' | 'center' }> = [
  { id: 'bxwkiui0zplo7rexp1sp', gravity: 'center' },
  { id: 'buxxor3j5kmy5vqo9klc', gravity: 'center' },
  { id: 'byfoq3zduoearfn090g', gravity: 'north' },
  { id: 'l0clydv7291tl39ud6', gravity: 'center' },
  { id: 'wp4rzuxk8fzq899bpd', gravity: 'center' },
]

export const TOUR_DEFAULT_DEAL_HERO_IMAGE_ID = 'b1c309a3-eb76-4e00-8748-bdf59fd7d714'
export const TOUR_DEFAULT_DEAL_HIGHLIGHT_COLOUR = '#232B5C'

export const TOUR_ASIA_PROMO_REGIONS: Record<string, number> = {
  AU: 849,
  NZ: 920,
  US: 520,
  GB: 430,
  SG: 710,
  HK: 4090,
  CA: 760,
  FR: 490,
  IT: 490,
  ES: 490,
  NL: 490,
  DE: 490,
  IE: 490,
}
